import { Link } from 'react-router-dom';
import { ArrowRight, Lightbulb, DollarSign, MapPin, Users, Mic2, Monitor, UtensilsCrossed, Palette, Globe, Camera, UserCheck, Star } from 'lucide-react';

const services = [
  { 
    icon: Lightbulb, 
    title: 'Event Planning & Concept Development',
    description: 'We transform your vision into reality with creative ideation and strategic planning. Our team crafts unique event concepts that align with your brand identity and objectives.'
  },
  { 
    icon: DollarSign, 
    title: 'Budget Planning & Management',
    description: 'Comprehensive financial planning ensures maximum ROI for your events. We optimize costs without compromising quality, providing transparent budgeting and expense tracking.'
  },
  { 
    icon: MapPin, 
    title: 'Venue Sourcing & Booking',
    description: 'Access to premium venues worldwide with exclusive negotiated rates. We handle site visits, contract negotiations, and all logistics for seamless venue management.'
  },
  { 
    icon: Users, 
    title: 'Guest List & RSVP Management',
    description: 'Digital invitation management with real-time RSVP tracking and guest communication. We ensure smooth check-in processes and personalized guest experiences.'
  },
  { 
    icon: Mic2, 
    title: 'Vendor Coordination',
    description: 'End-to-end vendor management including sourcing, negotiation, and coordination. We work with trusted partners to deliver exceptional services for your event.'
  },
  { 
    icon: Monitor, 
    title: 'Audio-Visual Setup',
    description: 'State-of-the-art AV equipment including LED walls, sound systems, and lighting. Our technical team ensures flawless presentations and immersive experiences.'
  },
  { 
    icon: UtensilsCrossed, 
    title: 'Catering & Menu Planning',
    description: 'Curated culinary experiences from world-class caterers and celebrity chefs. We design menus that cater to diverse preferences and dietary requirements.'
  },
  { 
    icon: Palette, 
    title: 'Event Design & Decor',
    description: 'Bespoke event aesthetics including stage design, floral arrangements, and ambient lighting. We create visually stunning environments that leave lasting impressions.'
  },
  { 
    icon: Globe, 
    title: 'Event Website or App Development',
    description: 'Custom digital platforms for registration, engagement, and virtual attendance. We build user-friendly solutions that enhance attendee interaction and experience.'
  },
  { 
    icon: Camera, 
    title: 'Photography & Videography',
    description: 'Professional coverage including live streaming, drone footage, and post-production. We capture every memorable moment with cinematic quality and attention to detail.'
  },
  { 
    icon: UserCheck, 
    title: 'Hostess & Staffing',
    description: 'Trained event staff, hostesses, ushers, and customer service professionals. Our team ensures guests receive warm hospitality and seamless assistance throughout.'
  },
  { 
    icon: Star, 
    title: 'VIP & Celebrity Management',
    description: 'Red carpet services, celebrity coordination, and exclusive VIP experiences. We handle all aspects of high-profile guest management with discretion and professionalism.'
  },
];

export const ServicesPreviewSection = () => {
  return (
    <section data-testid="services-preview-section" className="py-24 bg-[#121212]">
      <div className="w-full px-4 sm:px-8 lg:px-16">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <p className="font-google-sans text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4">What We Offer</p>
            <h2 className="font-montserrat text-4xl sm:text-5xl lg:text-6xl text-white font-bold">
              Our <span className="gradient-gold">Services</span>
            </h2>
          </div>
          <Link
            to="/services"
            data-testid="view-all-services"
            className="inline-flex items-center gap-2 text-[#D4AF37] font-medium uppercase tracking-wider hover:gap-3 transition-all duration-300"
          >
            View All Services
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {services.map((service, index) => (
            <div
              key={index}
              data-testid={`service-preview-${index}`}
              className="group p-6 bg-white/[0.02] border border-white/10 hover:border-[#D4AF37]/50 transition-all duration-500"
            >
              <div className="w-16 h-16 border border-[#D4AF37]/30 flex items-center justify-center mb-5 group-hover:border-[#D4AF37] transition-colors">
                <service.icon className="w-8 h-8 text-[#D4AF37]" strokeWidth={1.5} />
              </div>
              <h3 className="font-montserrat text-white text-lg font-semibold mb-3 leading-tight">
                {service.title}
              </h3>
              <p className="font-google-sans text-[#A1A1AA] text-sm leading-relaxed text-justify">
                {service.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
